import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  closeBuyerRfq,
  confirmRfqCompletion,
  createRfq,
  getBuyerRfq,
  listBuyerRfqs,
  respondRfqSubstitution,
} from '../api/rfqs'
import {
  completeSupplierRfq,
  decideSupplierRfq,
  listSupplierInbox,
} from '../api/supplierInbox'
import type {
  AuthSession,
  RfqDto,
  RfqLineDto,
  SupplierRfqInboxItemDto,
  SupplierRfqRejectionReasonCode,
} from '../api/types'
import type { MessengerBridge } from '../bridge'
import type { UserContext } from '../hooks/useUserContext'

type OrdersScreenProps = {
  bridge: MessengerBridge
  session: AuthSession
  userContext: UserContext
  initialRfqId: number | null
  onOpenChat: (counterpartyOrganizationId: number, counterpartyName: string) => void
}

type ViewState =
  | { kind: 'list' }
  | { kind: 'buyer-detail'; rfq: RfqDto }
  | { kind: 'supplier-detail'; item: SupplierRfqInboxItemDto }

type RejectReasonOption = {
  code: SupplierRfqRejectionReasonCode
  label: string
}

const REJECT_REASONS: RejectReasonOption[] = [
  { code: 'out_of_stock', label: 'Нет в наличии' },
  { code: 'no_delivery_to_address', label: 'Не доставляем по адресу' },
  { code: 'min_order_not_met', label: 'Минимальная сумма заказа' },
  { code: 'discontinued', label: 'Товар снят с ассортимента' },
  { code: 'other', label: 'Другое' },
]

const formatStatus = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'open':
      return 'Открыта'
    case 'draft':
      return 'Черновик'
    case 'closed':
      return 'Закрыта'
    case 'pending':
      return 'Ожидает решения'
    case 'accepted':
      return 'В работе'
    case 'rejected':
      return 'Отклонена'
    case 'awaiting_customer_confirmation':
      return 'Ждёт подтверждения'
    case 'disputed':
      return 'Есть замечания'
    case 'completed':
      return 'Выполнена'
    case 'substitution_proposed':
      return 'Предложена замена'
    case 'cancelled':
      return 'Отменена'
    default:
      return status
  }
}

const formatRejectionReason = (code: string | null | undefined): string | null => {
  if (!code) {
    return null
  }
  const match = REJECT_REASONS.find((reason) => reason.code === code)
  return match?.label ?? code
}

const statusBadgeClass = (status: string): string => {
  switch (status.toLowerCase()) {
    case 'open':
    case 'accepted':
    case 'completed':
      return 'echat-badge echat-badge--success'
    case 'rejected':
    case 'closed':
      return 'echat-badge echat-badge--danger'
    case 'awaiting_customer_confirmation':
    case 'disputed':
    case 'substitution_proposed':
      return 'echat-badge echat-badge--warning'
    case 'pending':
    case 'draft':
    default:
      return 'echat-badge echat-badge--muted'
  }
}

const ARCHIVED_RFQ_STATUSES = new Set(['closed', 'cancelled'])
const ARCHIVED_SUPPLIER_DECISIONS = new Set(['rejected', 'completed', 'cancelled'])
const ARCHIVED_RESTAURANT_DECISIONS = new Set(['rejected', 'completed', 'cancelled'])

/** Статусы, требующие действия пользователя, — такие заявки поднимаем наверх списка. */
const ACTIONABLE_BUYER_DECISIONS = new Set(['substitution_proposed', 'awaiting_customer_confirmation'])

const buyerNeedsAction = (rfq: RfqDto): boolean =>
  ACTIONABLE_BUYER_DECISIONS.has(buyerSupplierDecision(rfq))

const supplierNeedsAction = (item: SupplierRfqInboxItemDto): boolean =>
  supplierDecisionStatus(item) === 'pending'

const byNeedsActionThenNewest = <T,>(
  needsAction: (item: T) => boolean,
  createdAt: (item: T) => string,
) =>
  (a: T, b: T): number => {
    const actionDelta = Number(needsAction(b)) - Number(needsAction(a))
    if (actionDelta !== 0) {
      return actionDelta
    }
    return createdAt(b).localeCompare(createdAt(a))
  }

const supplierDecisionStatus = (item: SupplierRfqInboxItemDto): string =>
  (item.decisionStatus ?? 'pending').toLowerCase()

const supplierRfqStatus = (item: SupplierRfqInboxItemDto): string =>
  (item.rfqStatus ?? 'open').toLowerCase()

const buyerSupplierDecision = (rfq: RfqDto): string =>
  (rfq.supplierDecisionStatus ?? '').toLowerCase()

const isArchivedSupplierInbox = (item: SupplierRfqInboxItemDto): boolean => {
  const decision = supplierDecisionStatus(item)
  const rfqStatus = supplierRfqStatus(item)
  if (ARCHIVED_SUPPLIER_DECISIONS.has(decision)) {
    return true
  }
  if (ARCHIVED_RFQ_STATUSES.has(rfqStatus)) {
    return true
  }
  return false
}

const isArchivedBuyerRfq = (rfq: RfqDto): boolean => {
  const status = rfq.status.toLowerCase()
  if (ARCHIVED_RFQ_STATUSES.has(status)) {
    return true
  }
  const decision = buyerSupplierDecision(rfq)
  if (decision && ARCHIVED_RESTAURANT_DECISIONS.has(decision)) {
    return true
  }
  return false
}

const pluralizeLines = (count: number): string => {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) {
    return 'позиция'
  }
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return 'позиции'
  }
  return 'позиций'
}

const formatDate = (value: string): string => {
  try {
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(value))
  } catch {
    return value
  }
}

const formatDeliveryDate = (isoDate: string): string => {
  try {
    const [year, month, day] = isoDate.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return new Intl.DateTimeFormat('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    }).format(date)
  } catch {
    return isoDate
  }
}

const formatDeliveryWindow = (
  date: string | null | undefined,
  timeUntil: string | null | undefined,
): string | null => {
  if (!date) {
    return null
  }
  const formattedDate = formatDeliveryDate(date)
  if (timeUntil) {
    const time = timeUntil.length >= 5 ? timeUntil.slice(0, 5) : timeUntil
    return `Желаемая доставка: ${formattedDate} до ${time}`
  }
  return `Желаемая доставка: ${formattedDate}`
}

const formatLineLabel = (line: RfqLineDto): string => {
  const product = line.productName?.trim()
  const sku = line.supplierSku?.trim()
  const title =
    product && sku ? `${product} · ${sku}` : product || sku || `Позиция #${line.id}`
  const qty = `${line.quantity} ${line.unit}`.trim()
  return line.note ? `${title} — ${qty} · ${line.note}` : `${title} — ${qty}`
}

const isExcludedLine = (line: RfqLineDto): boolean =>
  (line.lineStatus ?? 'active').toLowerCase() === 'excluded'

const activeLines = (lines: RfqLineDto[]): RfqLineDto[] =>
  lines.filter((line) => !isExcludedLine(line))

const excludedLines = (lines: RfqLineDto[]): RfqLineDto[] =>
  lines.filter((line) => isExcludedLine(line))

const buyerDisplayName = (item: SupplierRfqInboxItemDto): string => {
  if (item.buyerTradeName?.trim()) {
    return item.buyerTradeName.trim()
  }
  if (item.buyerLegalName?.trim()) {
    return item.buyerLegalName.trim()
  }
  return item.buyerOrganizationName
}

export const OrdersScreen = ({
  bridge,
  session,
  userContext,
  initialRfqId,
  onOpenChat,
}: OrdersScreenProps) => {
  const [view, setView] = useState<ViewState>({ kind: 'list' })
  const [buyerOrders, setBuyerOrders] = useState<RfqDto[]>([])
  const [supplierOrders, setSupplierOrders] = useState<SupplierRfqInboxItemDto[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [supplierMessage, setSupplierMessage] = useState('')
  const [archiveExpanded, setArchiveExpanded] = useState(false)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [rejectReason, setRejectReason] = useState<SupplierRfqRejectionReasonCode | null>(null)
  const [rejectMessage, setRejectMessage] = useState('')
  const [disputeOpen, setDisputeOpen] = useState(false)
  const [disputeComment, setDisputeComment] = useState('')
  const [substitutionOpen, setSubstitutionOpen] = useState(false)
  const [substitutionMessage, setSubstitutionMessage] = useState('')
  const [excludeMode, setExcludeMode] = useState(false)
  const [excludedLineIds, setExcludedLineIds] = useState<number[]>([])
  const [cancelConfirm, setCancelConfirm] = useState<'cancel' | 'substitution-cancel' | null>(null)
  const [repeatOpen, setRepeatOpen] = useState(false)
  const [repeatDate, setRepeatDate] = useState('')

  const organizationId = session.organizationId
  const isSupplier = userContext.role === 'supplier'

  const { activeBuyerOrders, archivedBuyerOrders } = useMemo(() => {
    const active: RfqDto[] = []
    const archived: RfqDto[] = []
    for (const rfq of buyerOrders) {
      if (isArchivedBuyerRfq(rfq)) {
        archived.push(rfq)
      } else {
        active.push(rfq)
      }
    }
    active.sort(byNeedsActionThenNewest(buyerNeedsAction, (rfq) => rfq.createdAt))
    return { activeBuyerOrders: active, archivedBuyerOrders: archived }
  }, [buyerOrders])

  const { activeSupplierOrders, archivedSupplierOrders } = useMemo(() => {
    const active: SupplierRfqInboxItemDto[] = []
    const archived: SupplierRfqInboxItemDto[] = []
    for (const item of supplierOrders) {
      if (isArchivedSupplierInbox(item)) {
        archived.push(item)
      } else {
        active.push(item)
      }
    }
    active.sort(byNeedsActionThenNewest(supplierNeedsAction, (item) => item.createdAt))
    return { activeSupplierOrders: active, archivedSupplierOrders: archived }
  }, [supplierOrders])

  const loadList = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      if (isSupplier) {
        const page = await listSupplierInbox(organizationId, 0, 200)
        setSupplierOrders(page.items)
      } else {
        const items = await listBuyerRfqs(organizationId)
        setBuyerOrders(items)
      }
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить заявки')
    } finally {
      setLoading(false)
    }
  }, [isSupplier, organizationId])

  const refreshSupplierItem = useCallback(
    async (inboxId: number) => {
      const page = await listSupplierInbox(organizationId)
      const refreshed = page.items.find((entry) => entry.inboxId === inboxId)
      setSupplierOrders(page.items)
      if (refreshed) {
        setView({ kind: 'supplier-detail', item: refreshed })
      } else {
        setView({ kind: 'list' })
      }
    },
    [organizationId],
  )

  useEffect(() => {
    if (userContext.loading) {
      return
    }
    void loadList()
  }, [loadList, userContext.loading])

  useEffect(() => {
    if (view.kind === 'list') {
      return
    }
    bridge.setBackHandler(() => setView({ kind: 'list' }))
    return () => bridge.setBackHandler(null)
  }, [bridge, view.kind])

  useEffect(() => {
    if (!initialRfqId || userContext.loading) {
      return
    }

    const openInitial = async () => {
      try {
        if (isSupplier) {
          const page = await listSupplierInbox(organizationId, 0, 200)
          const item = page.items.find((entry) => entry.rfqId === initialRfqId)
          if (item) {
            setView({ kind: 'supplier-detail', item })
          }
        } else {
          const rfq = await getBuyerRfq(initialRfqId)
          setView({ kind: 'buyer-detail', rfq })
        }
      } catch {
        // Deep-link fallback: stay on list if detail is unavailable.
      }
    }

    void openInitial()
  }, [initialRfqId, isSupplier, organizationId, userContext.loading])

  const handleBuyerCancel = async (rfq: RfqDto) => {
    setCancelConfirm(null)
    setActionLoading(true)
    setError(null)

    try {
      const updated = await closeBuyerRfq(rfq.id)
      setView({ kind: 'buyer-detail', rfq: updated })
      await loadList()
    } catch (cancelError) {
      setError(cancelError instanceof Error ? cancelError.message : 'Не удалось отменить заявку')
    } finally {
      setActionLoading(false)
    }
  }

  const handleSupplierAccept = async (item: SupplierRfqInboxItemDto) => {
    setActionLoading(true)
    setError(null)

    try {
      await decideSupplierRfq(item.inboxId, organizationId, 'accept', {
        message: supplierMessage,
      })
      setSupplierMessage('')
      await refreshSupplierItem(item.inboxId)
    } catch (decisionError) {
      setError(decisionError instanceof Error ? decisionError.message : 'Не удалось принять заявку')
    } finally {
      setActionLoading(false)
    }
  }

  const handleSupplierReject = async (item: SupplierRfqInboxItemDto) => {
    if (!rejectReason) {
      setError('Выберите причину отказа')
      return
    }
    if (rejectReason === 'other' && !rejectMessage.trim()) {
      setError('Опишите причину отказа')
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      await decideSupplierRfq(item.inboxId, organizationId, 'reject', {
        message: rejectMessage || supplierMessage,
        reasonCode: rejectReason,
      })
      setSupplierMessage('')
      setRejectMessage('')
      setRejectReason(null)
      setRejectOpen(false)
      await refreshSupplierItem(item.inboxId)
    } catch (decisionError) {
      setError(decisionError instanceof Error ? decisionError.message : 'Не удалось отклонить заявку')
    } finally {
      setActionLoading(false)
    }
  }

  const handleSupplierComplete = async (item: SupplierRfqInboxItemDto) => {
    setActionLoading(true)
    setError(null)

    try {
      await completeSupplierRfq(item.inboxId, organizationId, supplierMessage)
      setSupplierMessage('')
      await refreshSupplierItem(item.inboxId)
    } catch (completeError) {
      setError(
        completeError instanceof Error ? completeError.message : 'Не удалось отметить выполнение',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleSupplierProposeSubstitution = async (item: SupplierRfqInboxItemDto) => {
    const message = substitutionMessage.trim()
    if (message.length < 10) {
      setError('Опишите предлагаемую замену (не менее 10 символов)')
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      await decideSupplierRfq(item.inboxId, organizationId, 'propose_substitution', { message })
      setSubstitutionMessage('')
      setSubstitutionOpen(false)
      setSupplierMessage('')
      await refreshSupplierItem(item.inboxId)
    } catch (proposalError) {
      setError(
        proposalError instanceof Error ? proposalError.message : 'Не удалось отправить предложение замены',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleBuyerAcceptSubstitution = async (rfq: RfqDto) => {
    setActionLoading(true)
    setError(null)

    try {
      const updated = await respondRfqSubstitution(rfq.id, 'accept')
      setExcludeMode(false)
      setExcludedLineIds([])
      setView({ kind: 'buyer-detail', rfq: updated })
      await loadList()
    } catch (acceptError) {
      setError(
        acceptError instanceof Error ? acceptError.message : 'Не удалось принять замену',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleBuyerExcludeLines = async (rfq: RfqDto) => {
    if (excludedLineIds.length === 0) {
      setError('Выберите позиции, которые нужно исключить из заявки')
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      const updated = await respondRfqSubstitution(rfq.id, 'exclude_lines', {
        excludedLineIds,
      })
      setExcludeMode(false)
      setExcludedLineIds([])
      setView({ kind: 'buyer-detail', rfq: updated })
      await loadList()
    } catch (excludeError) {
      setError(
        excludeError instanceof Error ? excludeError.message : 'Не удалось исключить позиции',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleBuyerCancelSubstitution = async (rfq: RfqDto) => {
    setCancelConfirm(null)
    setActionLoading(true)
    setError(null)

    try {
      const updated = await respondRfqSubstitution(rfq.id, 'cancel')
      setExcludeMode(false)
      setExcludedLineIds([])
      setView({ kind: 'buyer-detail', rfq: updated })
      await loadList()
    } catch (cancelError) {
      setError(
        cancelError instanceof Error ? cancelError.message : 'Не удалось отказаться от заявки',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const toggleExcludedLine = (lineId: number) => {
    setExcludedLineIds((current) =>
      current.includes(lineId) ? current.filter((id) => id !== lineId) : [...current, lineId],
    )
  }

  const handleBuyerConfirm = async (rfq: RfqDto) => {
    setActionLoading(true)
    setError(null)

    try {
      const updated = await confirmRfqCompletion(rfq.id, 'confirm')
      setView({ kind: 'buyer-detail', rfq: updated })
      await loadList()
    } catch (confirmError) {
      setError(
        confirmError instanceof Error ? confirmError.message : 'Не удалось подтвердить выполнение',
      )
    } finally {
      setActionLoading(false)
    }
  }

  const handleBuyerDispute = async (rfq: RfqDto) => {
    if (!disputeComment.trim()) {
      setError('Опишите замечания к поставке')
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      const updated = await confirmRfqCompletion(rfq.id, 'dispute', disputeComment)
      setDisputeComment('')
      setDisputeOpen(false)
      setView({ kind: 'buyer-detail', rfq: updated })
      await loadList()
    } catch (disputeError) {
      setError(disputeError instanceof Error ? disputeError.message : 'Не удалось отправить замечания')
    } finally {
      setActionLoading(false)
    }
  }

  const renderRejectModal = (item: SupplierRfqInboxItemDto) => {
    if (!rejectOpen) {
      return null
    }

    return (
      <div className="echat-modal" role="dialog" aria-modal="true" aria-labelledby="reject-title">
        <div className="echat-modal__backdrop" onClick={() => setRejectOpen(false)} />
        <div className="echat-modal__panel">
          <h3 id="reject-title" className="echat-modal__title">
            Причина отказа
          </h3>
          <p className="echat-modal__text">
            Ресторан увидит выбранную причину. При необходимости добавьте комментарий.
          </p>
          <div className="echat-chips" role="group" aria-label="Причины отказа">
            {REJECT_REASONS.map((reason) => (
              <button
                key={reason.code}
                type="button"
                className={
                  rejectReason === reason.code ? 'echat-chip echat-chip--selected' : 'echat-chip'
                }
                aria-pressed={rejectReason === reason.code}
                onClick={() => setRejectReason(reason.code)}
              >
                {reason.label}
              </button>
            ))}
          </div>
          {rejectReason === 'other' || rejectReason ? (
            <label className="echat-field">
              <span className="echat-field__label">
                {rejectReason === 'other' ? 'Комментарий (обязательно)' : 'Комментарий (необязательно)'}
              </span>
              <textarea
                className="echat-field__input"
                rows={3}
                maxLength={500}
                value={rejectMessage}
                onChange={(event) => setRejectMessage(event.target.value)}
                placeholder={
                  rejectReason === 'other'
                    ? 'Опишите причину отказа'
                    : 'Дополнительные детали для ресторана'
                }
              />
            </label>
          ) : null}
          <div className="echat-actions">
            <button
              type="button"
              className="echat-btn echat-btn--danger"
              disabled={actionLoading || !rejectReason}
              onClick={() => void handleSupplierReject(item)}
            >
              Отклонить заявку
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={actionLoading}
              onClick={() => {
                setRejectOpen(false)
                setRejectReason(null)
                setRejectMessage('')
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    )
  }

  const openRepeatSheet = () => {
    const tomorrow = new Date()
    tomorrow.setDate(tomorrow.getDate() + 1)
    setRepeatDate(tomorrow.toISOString().slice(0, 10))
    setError(null)
    setRepeatOpen(true)
  }

  const repeatableLines = (rfq: RfqDto): RfqLineDto[] =>
    activeLines(rfq.lines).filter((line) => line.productTypeId > 0)

  const handleRepeatRfq = async (rfq: RfqDto) => {
    const lines = repeatableLines(rfq)
    if (lines.length === 0) {
      setError('В заявке нет позиций, которые можно повторить')
      return
    }

    setActionLoading(true)
    setError(null)

    try {
      const created = await createRfq({
        organizationId,
        supplierOrganizationId: rfq.supplierOrganizationId,
        title: rfq.title,
        description: rfq.description,
        desiredDeliveryDate: repeatDate || null,
        deliveryAddress: rfq.deliveryAddress,
        lines: lines.map((line) => ({
          productTypeId: line.productTypeId,
          quantity: line.quantity,
          unit: line.unit,
          note: line.note,
        })),
      })
      setRepeatOpen(false)
      setView({ kind: 'buyer-detail', rfq: created })
      await loadList()
    } catch (repeatError) {
      setError(repeatError instanceof Error ? repeatError.message : 'Не удалось создать заявку')
    } finally {
      setActionLoading(false)
    }
  }

  const renderRepeatModal = (rfq: RfqDto) => {
    if (!repeatOpen) {
      return null
    }
    const lines = repeatableLines(rfq)

    return (
      <div className="echat-modal" role="dialog" aria-modal="true" aria-labelledby="repeat-title">
        <div className="echat-modal__backdrop" onClick={() => setRepeatOpen(false)} />
        <div className="echat-modal__panel">
          <h3 id="repeat-title" className="echat-modal__title">
            Повторить заявку
          </h3>
          <p className="echat-modal__text">
            Поставщик получит новую заявку с теми же позициями ({lines.length}{' '}
            {pluralizeLines(lines.length)}).
          </p>
          <label className="echat-field">
            <span className="echat-field__label">Желаемая дата доставки</span>
            <input
              type="date"
              className="echat-field__input"
              value={repeatDate}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(event) => setRepeatDate(event.target.value)}
            />
          </label>
          <ul className="echat-lines">
            {lines.map((line) => (
              <li key={line.id}>{formatLineLabel(line)}</li>
            ))}
          </ul>
          {error ? <p className="echat-error" role="alert">{error}</p> : null}
          <div className="echat-actions">
            <button
              type="button"
              className="echat-btn echat-btn--primary"
              disabled={actionLoading || lines.length === 0}
              onClick={() => void handleRepeatRfq(rfq)}
            >
              Отправить поставщику
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={actionLoading}
              onClick={() => setRepeatOpen(false)}
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderCancelConfirmModal = (rfq: RfqDto) => {
    if (!cancelConfirm) {
      return null
    }
    const isSubstitution = cancelConfirm === 'substitution-cancel'

    return (
      <div className="echat-modal" role="dialog" aria-modal="true" aria-labelledby="cancel-confirm-title">
        <div className="echat-modal__backdrop" onClick={() => setCancelConfirm(null)} />
        <div className="echat-modal__panel">
          <h3 id="cancel-confirm-title" className="echat-modal__title">
            {isSubstitution ? 'Отказаться от заявки?' : 'Отменить заявку?'}
          </h3>
          <p className="echat-modal__text">
            {isSubstitution
              ? 'Заявка будет закрыта, предложенная замена отклонена. Поставщик получит уведомление.'
              : 'Поставщик увидит, что заявка закрыта. Действие нельзя отменить.'}
          </p>
          <div className="echat-actions">
            <button
              type="button"
              className="echat-btn echat-btn--danger"
              disabled={actionLoading}
              onClick={() =>
                void (isSubstitution ? handleBuyerCancelSubstitution(rfq) : handleBuyerCancel(rfq))
              }
            >
              {isSubstitution ? 'Отказаться от заявки' : 'Отменить заявку'}
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={actionLoading}
              onClick={() => setCancelConfirm(null)}
            >
              Оставить
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderDeliveryBlock = (
    date: string | null | undefined,
    timeUntil: string | null | undefined,
    address: string | null | undefined,
  ) => {
    const deliveryLine = formatDeliveryWindow(date, timeUntil)
    if (!deliveryLine && !address) {
      return null
    }

    return (
      <div className="echat-panel__block">
        {deliveryLine ? <p className="echat-panel__text">{deliveryLine}</p> : null}
        {address ? <p className="echat-panel__text">Адрес: {address}</p> : null}
      </div>
    )
  }

  const renderLines = (
    lines: RfqLineDto[],
    options?: { selectable?: boolean; selectedIds?: number[] },
  ) => {
    if (lines.length === 0) {
      return null
    }

    if (options?.selectable) {
      return (
        <ul className="echat-lines echat-lines--selectable">
          {lines.map((line) => (
            <li key={line.id}>
              <label className="echat-line-check">
                <input
                  type="checkbox"
                  checked={options.selectedIds?.includes(line.id) ?? false}
                  onChange={() => toggleExcludedLine(line.id)}
                />
                <span>{formatLineLabel(line)}</span>
              </label>
            </li>
          ))}
        </ul>
      )
    }

    return (
      <ul className="echat-lines">
        {lines.map((line) => (
          <li key={line.id}>{formatLineLabel(line)}</li>
        ))}
      </ul>
    )
  }

  const renderSubstitutionModal = (item: SupplierRfqInboxItemDto) => {
    if (!substitutionOpen) {
      return null
    }

    return (
      <div className="echat-modal" role="dialog" aria-modal="true" aria-labelledby="substitution-title">
        <div className="echat-modal__backdrop" onClick={() => setSubstitutionOpen(false)} />
        <div className="echat-modal__panel">
          <h3 id="substitution-title" className="echat-modal__title">
            Предложить замену
          </h3>
          <p className="echat-modal__text">
            Опишите, какие позиции меняются и на что. Ресторан сможет принять замену, исключить
            позиции или отказаться от заявки.
          </p>
          <label className="echat-field">
            <span className="echat-field__label">Комментарий (обязательно)</span>
            <textarea
              className="echat-field__input"
              rows={4}
              maxLength={1000}
              value={substitutionMessage}
              onChange={(event) => setSubstitutionMessage(event.target.value)}
              placeholder="Например: филе заменим на бедро, томаты — на огурцы"
            />
          </label>
          <div className="echat-actions">
            <button
              type="button"
              className="echat-btn echat-btn--primary"
              disabled={actionLoading || substitutionMessage.trim().length < 10}
              onClick={() => void handleSupplierProposeSubstitution(item)}
            >
              Отправить предложение
            </button>
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={actionLoading}
              onClick={() => {
                setSubstitutionOpen(false)
                setSubstitutionMessage('')
              }}
            >
              Отмена
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (userContext.loading || loading) {
    return <p className="echat-orders__status">Загрузка заявок…</p>
  }

  if (view.kind === 'buyer-detail') {
    const { rfq } = view
    const decision = buyerSupplierDecision(rfq)
    const canCancel =
      (rfq.status === 'open' || rfq.status === 'draft') &&
      (!decision || decision === 'pending')
    const awaitingConfirmation = decision === 'awaiting_customer_confirmation'
    const substitutionProposed = decision === 'substitution_proposed'
    const displayStatus = awaitingConfirmation
      ? 'awaiting_customer_confirmation'
      : substitutionProposed
        ? 'substitution_proposed'
        : rfq.status
    const rejectionLabel = formatRejectionReason(rfq.rejectionReasonCode)
    const rfqActiveLines = activeLines(rfq.lines)

    return (
      <section aria-label={`Заявка ${rfq.id}`} className="echat-orders">
        <button type="button" className="echat-back" onClick={() => setView({ kind: 'list' })}>
          ← К списку
        </button>

        <article className="echat-panel">
          <span className={statusBadgeClass(displayStatus)}>
            {formatStatus(substitutionProposed ? decision : awaitingConfirmation ? decision : rfq.status)}
          </span>
          <h2 className="echat-panel__title">{rfq.title}</h2>
          {rfq.description ? <p className="echat-panel__text">{rfq.description}</p> : null}
          <p className="echat-panel__meta">Создана: {formatDate(rfq.createdAt)}</p>
          {rfq.supplierContact?.supplierName ? (
            <p className="echat-panel__text">Поставщик: {rfq.supplierContact.supplierName}</p>
          ) : null}
          {renderDeliveryBlock(
            rfq.desiredDeliveryDate,
            rfq.desiredDeliveryTimeUntil,
            rfq.deliveryAddress,
          )}
          {rejectionLabel ? (
            <p className="echat-panel__text">Причина отказа: {rejectionLabel}</p>
          ) : null}
          {rfq.customerDisputeComment ? (
            <p className="echat-panel__text">Ваши замечания: {rfq.customerDisputeComment}</p>
          ) : null}
          {rfq.substitutionComment ? (
            <p className="echat-panel__text">Предложение замены: {rfq.substitutionComment}</p>
          ) : null}
          {excludeMode ? (
            <>
              <p className="echat-panel__text">
                Выберите позиции, которые нужно исключить из заявки
              </p>
              {renderLines(rfqActiveLines, { selectable: true, selectedIds: excludedLineIds })}
            </>
          ) : (
            renderLines(rfq.lines)
          )}
        </article>

        {substitutionProposed && !excludeMode ? (
          <article className="echat-panel echat-panel--highlight">
            <h3 className="echat-panel__subtitle">Поставщик предложил замену</h3>
            <p className="echat-panel__text">
              Примите замену целиком, исключите отдельные позиции или откажитесь от заявки.
            </p>
          </article>
        ) : null}

        {awaitingConfirmation ? (
          <article className="echat-panel echat-panel--highlight">
            <h3 className="echat-panel__subtitle">Поставщик отметил заявку выполненной</h3>
            <p className="echat-panel__text">
              Проверьте, что заказ доставлен полностью и в согласованном объёме. Если всё в порядке —
              подтвердите выполнение. Если есть расхождения — опишите замечания, и поставщик свяжется с
              вами для уточнения.
            </p>
            {disputeOpen ? (
              <label className="echat-field">
                <span className="echat-field__label">Замечания к поставке</span>
                <textarea
                  className="echat-field__input"
                  rows={4}
                  maxLength={500}
                  value={disputeComment}
                  onChange={(event) => setDisputeComment(event.target.value)}
                  placeholder="Что не так: недовоз, брак, неверная позиция…"
                />
              </label>
            ) : null}
          </article>
        ) : null}

        {error ? <p className="echat-error" role="alert">{error}</p> : null}

        <div className="echat-actions">
          {substitutionProposed ? (
            excludeMode ? (
              <>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary"
                  disabled={actionLoading || excludedLineIds.length === 0}
                  onClick={() => void handleBuyerExcludeLines(rfq)}
                >
                  Продолжить без выбранных позиций
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={actionLoading}
                  onClick={() => {
                    setExcludeMode(false)
                    setExcludedLineIds([])
                  }}
                >
                  Отмена
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary"
                  disabled={actionLoading}
                  onClick={() => void handleBuyerAcceptSubstitution(rfq)}
                >
                  Принять замену
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={actionLoading || rfqActiveLines.length < 2}
                  onClick={() => {
                    setExcludeMode(true)
                    setExcludedLineIds([])
                    setError(null)
                  }}
                >
                  Исключить позиции и продолжить
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--danger"
                  disabled={actionLoading}
                  onClick={() => setCancelConfirm('substitution-cancel')}
                >
                  Отказаться от заявки
                </button>
              </>
            )
          ) : null}

          {awaitingConfirmation ? (
            disputeOpen ? (
              <>
                <button
                  type="button"
                  className="echat-btn echat-btn--danger"
                  disabled={actionLoading}
                  onClick={() => void handleBuyerDispute(rfq)}
                >
                  Отправить замечания
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={actionLoading}
                  onClick={() => {
                    setDisputeOpen(false)
                    setDisputeComment('')
                  }}
                >
                  Отмена
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="echat-btn echat-btn--primary"
                  disabled={actionLoading}
                  onClick={() => void handleBuyerConfirm(rfq)}
                >
                  Да, всё получено
                </button>
                <button
                  type="button"
                  className="echat-btn echat-btn--ghost"
                  disabled={actionLoading}
                  onClick={() => setDisputeOpen(true)}
                >
                  Есть замечания
                </button>
              </>
            )
          ) : null}

          {isArchivedBuyerRfq(rfq) && repeatableLines(rfq).length > 0 ? (
            <button
              type="button"
              className="echat-btn echat-btn--primary"
              disabled={actionLoading}
              onClick={openRepeatSheet}
            >
              Повторить заявку
            </button>
          ) : null}

          <button
            type="button"
            className={
              isArchivedBuyerRfq(rfq) ? 'echat-btn echat-btn--ghost' : 'echat-btn echat-btn--primary'
            }
            disabled={actionLoading}
            onClick={() =>
              onOpenChat(
                rfq.supplierOrganizationId,
                rfq.supplierContact?.supplierName?.trim() || `Поставщик #${rfq.supplierOrganizationId}`,
              )
            }
          >
            Написать поставщику
          </button>

          {canCancel ? (
            <button
              type="button"
              className="echat-btn echat-btn--danger"
              disabled={actionLoading}
              onClick={() => setCancelConfirm('cancel')}
            >
              Отменить заявку
            </button>
          ) : null}

          <button type="button" className="echat-btn echat-btn--ghost" onClick={() => bridge.close()}>
            Закрыть
          </button>
        </div>

        {renderCancelConfirmModal(rfq)}
        {renderRepeatModal(rfq)}
      </section>
    )
  }

  if (view.kind === 'supplier-detail') {
    const { item } = view
    const decision = supplierDecisionStatus(item)
    const canDecide = decision === 'pending'
    const isAccepted = decision === 'accepted' || decision === 'disputed'
    const isSubstitutionProposed = decision === 'substitution_proposed'
    const isAwaitingConfirmation = decision === 'awaiting_customer_confirmation'
    const rejectionLabel = formatRejectionReason(item.rejectionReasonCode)
    const clientName = buyerDisplayName(item)
    const itemExcludedLines = excludedLines(item.lines)
    const itemActiveLines = activeLines(item.lines)

    return (
      <section aria-label={`Входящая заявка ${item.rfqId}`} className="echat-orders">
        <button type="button" className="echat-back" onClick={() => setView({ kind: 'list' })}>
          ← К списку
        </button>

        <article className="echat-panel">
          <span className={statusBadgeClass(item.decisionStatus)}>{formatStatus(decision)}</span>
          <h2 className="echat-panel__title">{item.rfqTitle}</h2>
          <div className="echat-panel__block">
            <p className="echat-panel__label">Клиент</p>
            {item.buyerLegalName ? (
              <p className="echat-panel__text">{item.buyerLegalName}</p>
            ) : (
              <p className="echat-panel__text">{item.buyerOrganizationName}</p>
            )}
            {item.buyerTradeName ? (
              <p className="echat-panel__text echat-muted">«{item.buyerTradeName}»</p>
            ) : null}
          </div>
          {item.rfqDescription ? <p className="echat-panel__text">{item.rfqDescription}</p> : null}
          <p className="echat-panel__meta">Получена: {formatDate(item.createdAt)}</p>
          {renderDeliveryBlock(
            item.desiredDeliveryDate,
            item.desiredDeliveryTimeUntil,
            item.deliveryAddress,
          )}
          {item.decisionComment ? (
            <p className="echat-panel__text">Комментарий: {item.decisionComment}</p>
          ) : null}
          {rejectionLabel ? (
            <p className="echat-panel__text">Причина отказа: {rejectionLabel}</p>
          ) : null}
          {item.customerDisputeComment ? (
            <p className="echat-panel__text">Замечания ресторана: {item.customerDisputeComment}</p>
          ) : null}
          {item.substitutionComment ? (
            <p className="echat-panel__text">Ваше предложение замены: {item.substitutionComment}</p>
          ) : null}
          {itemExcludedLines.length > 0 ? (
            <div className="echat-panel__block">
              <p className="echat-panel__label">Ресторан исключил позиции:</p>
              <ul className="echat-lines">
                {itemExcludedLines.map((line) => (
                  <li key={line.id}>· {formatLineLabel(line)}</li>
                ))}
              </ul>
            </div>
          ) : null}
          {itemExcludedLines.length > 0 ? (
            <div className="echat-panel__block">
              <p className="echat-panel__label">Актуальная заявка:</p>
              {renderLines(itemActiveLines)}
            </div>
          ) : (
            renderLines(item.lines)
          )}
        </article>

        {canDecide ? (
          <label className="echat-field">
            <span className="echat-field__label">Сообщение ресторану при принятии (необязательно)</span>
            <textarea
              className="echat-field__input"
              rows={3}
              maxLength={500}
              value={supplierMessage}
              onChange={(event) => setSupplierMessage(event.target.value)}
              placeholder="Комментарий при принятии заявки"
            />
          </label>
        ) : null}

        {isSubstitutionProposed ? (
          <p className="echat-muted echat-orders__hint">
            Ожидаем ответ ресторана на предложение замены.
          </p>
        ) : null}

        {isAwaitingConfirmation ? (
          <p className="echat-muted echat-orders__hint">
            Ожидаем подтверждения от ресторана. При необходимости напишите в чат.
          </p>
        ) : null}

        {error ? <p className="echat-error" role="alert">{error}</p> : null}

        <div className="echat-actions">
          {canDecide ? (
            <>
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                disabled={actionLoading}
                onClick={() => void handleSupplierAccept(item)}
              >
                Принять
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--danger"
                disabled={actionLoading}
                onClick={() => {
                  setRejectOpen(true)
                  setError(null)
                }}
              >
                Отклонить
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--ghost"
                disabled={actionLoading}
                onClick={() => {
                  setSubstitutionOpen(true)
                  setError(null)
                }}
              >
                Предложить замену
              </button>
            </>
          ) : null}

          {isAccepted ? (
            <>
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                disabled={actionLoading}
                onClick={() =>
                  onOpenChat(item.buyerOrganizationId, clientName)
                }
              >
                Написать заказчику
              </button>
              <button
                type="button"
                className="echat-btn echat-btn--primary"
                disabled={actionLoading}
                onClick={() => void handleSupplierComplete(item)}
              >
                Выполнено
              </button>
            </>
          ) : null}

          {isAwaitingConfirmation || (!canDecide && !isAccepted) ? (
            <button
              type="button"
              className="echat-btn echat-btn--ghost"
              disabled={actionLoading}
              onClick={() => onOpenChat(item.buyerOrganizationId, clientName)}
            >
              Написать заказчику
            </button>
          ) : null}

          <button type="button" className="echat-btn echat-btn--ghost" onClick={() => bridge.close()}>
            Закрыть
          </button>
        </div>

        {renderRejectModal(item)}
        {renderSubstitutionModal(item)}
      </section>
    )
  }

  const renderSupplierRow = (item: SupplierRfqInboxItemDto) => {
    const decision = supplierDecisionStatus(item)
    const rfqStatus = supplierRfqStatus(item)
    const badgeStatus = rfqStatus !== 'open' && ARCHIVED_RFQ_STATUSES.has(rfqStatus) ? rfqStatus : decision

    return (
      <li key={item.inboxId} className="echat-inbox__item">
        <button
          type="button"
          className="echat-inbox__row"
          onClick={() => setView({ kind: 'supplier-detail', item })}
        >
          <span className="echat-inbox__body">
            <span className="echat-inbox__name">{item.rfqTitle}</span>
            <span className="echat-inbox__statusline">
              <span className={`${statusBadgeClass(badgeStatus)} echat-inbox__badge`}>
                {formatStatus(badgeStatus)}
              </span>
              <span className="echat-inbox__preview">
                {buyerDisplayName(item)} · {formatDate(item.createdAt)} · {item.lines.length}{' '}
                {pluralizeLines(item.lines.length)}
              </span>
            </span>
          </span>
        </button>
      </li>
    )
  }

  const renderBuyerRow = (rfq: RfqDto) => {
    const decision = buyerSupplierDecision(rfq)
    const badgeStatus =
      decision && (ACTIONABLE_BUYER_DECISIONS.has(decision) || ARCHIVED_RESTAURANT_DECISIONS.has(decision))
        ? decision
        : rfq.status

    return (
      <li key={rfq.id} className="echat-inbox__item">
        <button
          type="button"
          className="echat-inbox__row"
          onClick={() => setView({ kind: 'buyer-detail', rfq })}
        >
          <span className="echat-inbox__body">
            <span className="echat-inbox__name">{rfq.title}</span>
            <span className="echat-inbox__statusline">
              <span className={`${statusBadgeClass(badgeStatus)} echat-inbox__badge`}>
                {formatStatus(badgeStatus)}
              </span>
              <span className="echat-inbox__preview">
                №{rfq.id} · {formatDate(rfq.createdAt)} · {rfq.lines.length}{' '}
                {pluralizeLines(rfq.lines.length)}
              </span>
            </span>
          </span>
        </button>
      </li>
    )
  }

  const renderArchiveToggle = (count: number) => (
    <section className="echat-archive" aria-label="Архив заявок">
      <button
        type="button"
        className="echat-archive__toggle"
        aria-expanded={archiveExpanded}
        onClick={() => setArchiveExpanded((expanded) => !expanded)}
      >
        <span>{archiveExpanded ? 'Скрыть архив' : 'Архив'}</span>
        <span className="echat-archive__count">{count}</span>
        <span className="echat-archive__chevron" aria-hidden="true">
          {archiveExpanded ? '▾' : '▸'}
        </span>
      </button>
    </section>
  )

  const renderArchiveList = () => {
    if (archivedCount === 0) {
      return <p className="echat-archive__empty echat-muted">В архиве пока нет заявок</p>
    }

    return (
      <div className="echat-inbox echat-inbox--archive">
        <ul className="echat-inbox__list">
          {isSupplier
            ? archivedSupplierOrders.map(renderSupplierRow)
            : archivedBuyerOrders.map(renderBuyerRow)}
        </ul>
      </div>
    )
  }

  const activeCount = isSupplier ? activeSupplierOrders.length : activeBuyerOrders.length
  const archivedCount = isSupplier ? archivedSupplierOrders.length : archivedBuyerOrders.length
  const totalCount = activeCount + archivedCount

  return (
    <section aria-label="Список заявок" className="echat-orders echat-orders--list">
      <div className="echat-orders__scroll">
        {userContext.error ? <p className="echat-muted">{userContext.error}</p> : null}
        {error ? (
          <>
            <p className="echat-error" role="alert">{error}</p>
            <button type="button" className="echat-btn echat-btn--ghost" onClick={() => void loadList()}>
              Повторить
            </button>
          </>
        ) : null}

        {totalCount === 0 && !error ? (
          <div className="echat-inbox__empty">
            <p className="echat-inbox__empty-title">
              {isSupplier ? 'Входящих заявок нет' : 'Заявок пока нет'}
            </p>
            <p className="echat-muted">
              {isSupplier
                ? 'Новые заявки от ресторанов появятся здесь.'
                : 'Создайте заявку в личном кабинете Провизиум.'}
            </p>
          </div>
        ) : activeCount > 0 ? (
          <div className="echat-inbox">
            <ul className="echat-inbox__list">
              {isSupplier
                ? activeSupplierOrders.map(renderSupplierRow)
                : activeBuyerOrders.map(renderBuyerRow)}
            </ul>
          </div>
        ) : totalCount > 0 ? (
          <p className="echat-muted echat-orders__empty-active">Активных заявок нет.</p>
        ) : null}

        {totalCount > 0 ? renderArchiveToggle(archivedCount) : null}
        {archiveExpanded ? renderArchiveList() : null}
      </div>

      <div className="echat-orders__footer">
        <button type="button" className="echat-btn echat-btn--ghost" onClick={() => bridge.close()}>
          Закрыть
        </button>
      </div>
    </section>
  )
}
